import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { getAuthToken } from '../utils/authSession';
import './NotificationPreferences.css';

// Laatst opgehaalde voorkeuren: de modal toont ze meteen (geen "Laden...") en
// ververst op de achtergrond. Het profielmenu haalt ze al op bij het openen
// van het menu (prefetchNotificationPreferences), dus ze staan klaar nog
// voor je op "Meldingen instellen" tikt.
let prefsCache = null;

export const prefetchNotificationPreferences = async () => {
  try {
    const res = await axios.get(`${API_URL}/api/notification-preferences`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    prefsCache = res.data;
  } catch (e) { /* stil: de modal probeert het zelf nog eens */ }
};

// Modal om per notificatie-type (relevant voor de rol van de ingelogde
// gebruiker) aan/uit te zetten of hij die nog wil ontvangen. Elke toggle
// stuurt meteen de bijgewerkte voorkeuren door, geen aparte opslagknop nodig.
function NotificationPreferences({ onClose }) {
  const { t } = useTranslation();
  const [types, setTypes] = useState(prefsCache?.types || []);
  const [role, setRole] = useState(prefsCache?.role || '');
  const [prefs, setPrefs] = useState(prefsCache?.prefs || {});
  const [loading, setLoading] = useState(!prefsCache);
  const [error, setError] = useState('');
  const [savingType, setSavingType] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notification-preferences`, authHeaders());
        setTypes(res.data.types || []);
        setRole(res.data.role || '');
        setPrefs(res.data.prefs || {});
        prefsCache = res.data;
      } catch (err) {
        setError(t('notificationPrefs.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, [t]);

  const toggleType = async (type) => {
    const updated = { ...prefs, [type]: !prefs[type] };
    setPrefs(updated);
    setSavingType(type);
    try {
      await axios.post(`${API_URL}/api/notification-preferences`, { prefs: updated }, authHeaders());
      if (prefsCache) prefsCache = { ...prefsCache, prefs: updated };
    } catch (err) {
      // Terugdraaien bij mislukking
      setPrefs(prefs);
      setError(t('notificationPrefs.saveError'));
    } finally {
      setSavingType(null);
    }
  };

  return (
    <div className="notifprefs-overlay" onClick={onClose}>
      <div className="notifprefs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notifprefs-header">
          <h2>{t('notificationPrefs.title')}</h2>
          <button className="notifprefs-close" onClick={onClose}>&times;</button>
        </div>
        <div className="notifprefs-body">
          {loading && <div className="notifprefs-skeleton" aria-hidden="true"><span /><span /><span /></div>}
          {!loading && error && <p className="notifprefs-error">{error}</p>}
          {!loading && !error && types.length === 0 && (
            <p className="notifprefs-empty">{t('notificationPrefs.none')}</p>
          )}
          {!loading && types.map((type) => (
            <label className="notifprefs-row" key={type}>
              {/* Per rol een eigen, passende tekst (bv. schoonmaker: "Een admin
                  heeft je op een taak gezet"); zonder rolspecifieke tekst
                  valt het terug op de algemene tekst van dit type. */}
              <span>{t(`notificationPrefs.roleTypes.${role}.${type}`, { defaultValue: t(`notificationPrefs.types.${type}`, type) })}</span>
              <span className={`notifprefs-switch ${prefs[type] ? 'on' : ''} ${savingType === type ? 'saving' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!prefs[type]}
                  onChange={() => toggleType(type)}
                  disabled={savingType === type}
                />
                <span className="notifprefs-slider" />
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NotificationPreferences;
